import React, { useEffect, useState } from 'react';
import { Table, Button, Select, Space } from 'antd';
import { PlusOutlined, FileTextOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WelcomeBanner from '../../Components/common/WelcomeBanner';
import { ApiErrorState, EmptyState } from '../../Components/common/StateViews';
import StatusTag from '../../Components/common/StatusTag';
import useApiData from '../../Hooks/useApiData';
import cfoService from '../../Services/cfoService';
import cioService from '../../Services/cioService';
import CreateInvoiceModal from '../../Components/invoice/CreateInvoiceModal';
import {
  InvoiceStatusLabels,
  InvoiceStatusColors,
  InvoicePaymentMethodLabels,
  optionsFromLabels,
  labelOf,
} from '../../Constants/enums';
import { money, formatDate } from '../../Utils/format';

const STATUS_OPTIONS = [{ value: '', label: 'All statuses' }, ...optionsFromLabels(InvoiceStatusLabels)];

const Invoices = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClientId = searchParams.get('clientId');

  const [status, setStatus] = useState('');
  const [clientFilter, setClientFilter] = useState(queryClientId ? Number(queryClientId) : undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [customers, setCustomers] = useState([]);
  const [createOpen, setCreateOpen] = useState(searchParams.get('action') === 'create');

  useEffect(() => {
    cioService
      .getClients({ pageSize: 100 })
      .then((res) => setCustomers(res?.items || []))
      .catch(() => setCustomers([]));
  }, []);

  const { data, loading, error, reload } = useApiData(
    () => cfoService.getInvoices({ clientId: clientFilter, status, page, pageSize }),
    [clientFilter, status, page, pageSize]
  );

  const columns = [
    { title: 'Invoice #', dataIndex: 'invoiceNumber' },
    { title: 'Customer', dataIndex: 'clientName' },
    {
      title: 'Plan',
      key: 'plan',
      render: (_, r) => {
        const desc = r.lines?.[0]?.description || '—';
        return (
          <div title={desc} style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {desc}
          </div>
        );
      },
    },
    { title: 'Amount', dataIndex: 'total', render: (v, r) => money(v, r.currency) },
    { title: 'Issued', dataIndex: 'issueDate', render: formatDate },
    { title: 'Due', dataIndex: 'dueDate', render: formatDate },
    {
      title: 'Method',
      dataIndex: 'paymentMethod',
      render: (v) => (v === null || v === undefined ? 'Stripe' : labelOf(InvoicePaymentMethodLabels, v)),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v) => <StatusTag value={v} labels={InvoiceStatusLabels} colors={InvoiceStatusColors} />,
    },
  ];

  return (
    <div>
      <WelcomeBanner
        title="Invoices"
        subtitle="Preview, create, send, and confirm customer invoices"
        icon={<FileTextOutlined />}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Create Invoice
          </Button>
        }
      />

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          value={status}
          onChange={(v) => {
            setPage(1);
            setStatus(v);
          }}
          options={STATUS_OPTIONS}
          style={{ width: 200 }}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="All customers"
          style={{ width: 280 }}
          value={clientFilter}
          onChange={(v) => {
            setPage(1);
            setClientFilter(v);
          }}
          options={customers.map((c) => ({ value: c.id, label: `${c.clientName} (${c.accountNumber})` }))}
        />
      </Space>

      {error ? (
        <ApiErrorState error={error} onRetry={reload} />
      ) : (
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={data?.items || []}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <EmptyState description="No invoices for this filter." /> }}
          onRow={(record) => ({
            onClick: () => navigate(`/cfo/invoices/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: data?.page || page,
            pageSize: data?.pageSize || pageSize,
            total: data?.totalCount || 0,
            showSizeChanger: true,
            onChange: (nextPage, nextSize) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
        />
      )}

      <CreateInvoiceModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        presetClientId={clientFilter || null}
        onCreated={(invoice) => invoice?.id && navigate(`/cfo/invoices/${invoice.id}`)}
      />
    </div>
  );
};

export default Invoices;
